import React, { Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Form, FormControlProps } from 'react-bootstrap';
import i18n from 'i18n';
import { connect } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import { WithTranslation, withTranslation } from 'react-i18next';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import _ from 'lodash';
import { formattedText } from 'utils/commonUtil';
import Autofocus from 'components/forms/Autofocus/Autofocus';
import { ProcessingActionContext } from 'contexts/ProcessingActionContext';
import { addEntityToModel, addParameterToEntity } from 'store/dataModel/dataModelActions';
import { ManipulatorDataModel } from '@blazemeter/blaze-data-designer-client/src/index';
import blazeDataClient from 'blazeData/blazeDataClient';
import { MeModel } from '@blazemeter/blaze-data-model/src/model/DataModel';
import { EntityType } from 'types/rest/assetCatalogTypes';

interface LocalProps {
  itemName: string;
  closeModalAction: () => void;
  createEntityCallBack: (itemName: string) => void;
  /** List of items available in the creation dropdown (for validation purposes). */
  availableItems: string[];
  entityType: EntityType;
}

class EntityModal extends Component<AllProps> {
  private NAME_MAX_LENGTH = 250;

  state = {
    itemName: this.props.itemName,
    fullMatchFound: '',
    isItemNameValid: true,
    invalidErrorMessage: '',
  };

  static contextType = ProcessingActionContext;

  handleFieldChange = (event: React.FormEvent<FormControlProps>, names: string[]) => {
    const keyword: string = event.currentTarget.value as string;
    let fullMatch = false;
    let invalidErrorMessage = '';
    let isValidName = !!keyword.match('^(?![0-9])[a-zA-Z0-9$_]+$');
    if (isValidName && keyword.length > this.NAME_MAX_LENGTH) {
      isValidName = false;
      invalidErrorMessage = ` ${i18n.t('common:blazeDropdown.nameMaxLength', {
        maxLength: this.NAME_MAX_LENGTH,
      })}`;
    }
    if (isValidName && keyword.length > 1) {
      isValidName = !!keyword.trim().length;
    }
    for (const name of names) {
      if (name.toLocaleLowerCase() === keyword.toLowerCase().trim()) {
        fullMatch = true;
        break;
      }
    }
    if (isValidName) {
      isValidName = !keyword.startsWith(' ');
    }
    return {
      searchTerm: keyword,
      fullMatchFound: fullMatch,
      isValidaName: isValidName,
      invalidErrorMessage: invalidErrorMessage,
    };
  };

  /**
   * Search input field value has changed handler.
   * @param event
   */
  handleNameChange = (event: any) => {
    const elements = this.handleFieldChange(event, this.props.availableItems);
    let invalidErrorMessage = `${i18n.t('common:blazeDropdown.nameIsInvalid')} ${elements.invalidErrorMessage}`;
    let isValidaName = elements.isValidaName;
    if (elements.fullMatchFound) {
      invalidErrorMessage = i18n.t('common:blazeDropdown.nameAlreadyExists');
      isValidaName = false;
    }

    this.setState({
      itemName: elements.searchTerm,
      fullMatchFound: elements.fullMatchFound,
      isItemNameValid: isValidaName,
      invalidErrorMessage: invalidErrorMessage,
    });
  };

  errorHandler = (error: any, messagePrefix: string) => {
    this.props.closeModalAction();
    const errorMsg = _.get(error, 'response.data.error', error.toString());
    //If the name is occupied, do inline validation. Otherwise show error toast
    if (errorMsg.includes('already exists.')) {
      this.setState({
        isItemNameValid: false,
        invalidErrorMessage: `Entity ${this.state.itemName} already exists.`,
      });
    } else {
      this.props.showToastNotification(ToastType.Danger, messagePrefix + this.state.itemName, errorMsg);
    }
  };

  createModel = async (dmModel: any) => {
    const { updateModel } = this.context;
    await updateModel(dmModel);
    if (dmModel !== null) {
      this.props.showToastNotification(
        ToastType.Success,
        '',
        formattedText(i18n.t('common:blazeDropdown.creationSuccess'), this.state.itemName),
      );
    }
  };

  /**
   * Create Entity based on the type.
   */
  createEntityHandler = async () => {
    if (this.state.itemName.trim().length > 0) {
      try {
        const { originalDm, dataManipulatorModel } = this.context;
        let dmModel: ManipulatorDataModel | null = null;
        const entityType: EntityType = this.props.entityType;
        switch (entityType) {
          case 'DATA_ENTITY':
            const oddmodel = await blazeDataClient.manipulator.load(originalDm as MeModel);
            dmModel = await addEntityToModel(_.cloneDeep(oddmodel), this.state.itemName);
            await this.createModel(dmModel);
            break;
          case 'DATA_PARAMETER':
            const { currentEntity } = this.context;
            dmModel = await addParameterToEntity(_.cloneDeep(dataManipulatorModel), currentEntity, this.state.itemName);
            await this.createModel(dmModel);
            break;
        }
        this.props.createEntityCallBack(this.state.itemName);
      } catch (error) {
        this.errorHandler(error, 'Could not create new entity');
      } finally {
        this.props.closeModalAction();
      }
    } else {
      this.props.closeModalAction();
    }
  };

  onCancel = () => {
    this.props.closeModalAction();
  };

  warningMessages = () => {
    return (
      <div className="invalid-feedback" style={{ visibility: this.state.isItemNameValid ? 'hidden' : 'visible' }}>
        {this.state.invalidErrorMessage}
      </div>
    );
  };

  entityTitle = this.props.entityType === 'DATA_ENTITY' ? 'Data Entity' : 'Data Parameter';

  render() {
    const isValid = this.state.isItemNameValid ? '' : 'is-invalid';
    return (
      <Modal
        id="qa-modal-bulk-deploy-modal"
        show={true}
        centered={true}
        backdrop={true}
        onHide={() => this.onCancel()}
        size="sm"
      >
        <Modal.Header closeButton id="qa-rename-modal-btn-close-icon">
          <h5 className="modal-title">{i18n.t('common:blazeDropdown.addNewEntity', { entity: this.entityTitle })}</h5>
        </Modal.Header>
        <Modal.Body style={{ height: '90px' }}>
          <Form.Group>
            <Form.Label htmlFor={'rename-field'}>
              {i18n.t('common:blazeDropdown.entityName', { entity: this.entityTitle })}
            </Form.Label>
            <Autofocus>
              <Form.Control
                className={isValid}
                id={'rename-field'}
                placeholder={i18n.t('common:blazeDropdown.entityName', { entity: this.entityTitle })}
                onChange={this.handleNameChange}
                value={this.state.itemName}
                autoComplete={'off'}
              />
            </Autofocus>
          </Form.Group>
          {this.warningMessages()}
        </Modal.Body>
        <Modal.Footer>
          <Button id={'qa-rename-modal-btn-cancel'} variant="link" onClick={this.onCancel}>
            {i18n.t('common:servicePicker.cancel')}
          </Button>
          <Button
            id={'qa-rename-save'}
            onClick={this.createEntityHandler}
            variant="primary"
            disabled={!this.state.isItemNameValid || this.state.itemName.trim() === ''}
          >
            {i18n.t('common:servicePicker.save')}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => ({
  blazeUserConfig: state.blazeUserConfig.blazeUserConfig,
});

const mapDispatchToProps = {
  showToastNotification,
};
type MapToStateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type AllProps = LocalProps & MapToStateProps & DispatchProps & WithTranslation;

export default withTranslation('common')(connect(mapStateToProps, mapDispatchToProps)(EntityModal));
